import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  listAppSuitePublishedTemplates,
  listAppSuitePublishedDeviceTypeModals,
  listAppSuitePublishedComponentStyles,
  listAnnouncements,
  type DeployRecord,
} from '../services/appsuiteApi'
import './AppSuiteOverviewPage.css'

type AnnouncementItem = {
  id: string
  name: string
  assetType: string
  version: string
  time: string
}

const ASSET_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  'app-template': { label: 'App Template', icon: 'view_quilt' },
  'app_template': { label: 'App Template', icon: 'view_quilt' },
  'device-type-modal': { label: 'Device Modal', icon: 'devices' },
  'device_type_modal': { label: 'Device Modal', icon: 'devices' },
  'component-style': { label: 'Component Style', icon: 'palette' },
  'component_style': { label: 'Component Style', icon: 'palette' },
}

function toLocalTime(value?: string): string {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('zh-TW', { hour12: false })
}

function toAnnouncement(item: DeployRecord): AnnouncementItem {
  return {
    id: item.id,
    name: item.targetName || item.targetId || 'Asset',
    assetType: item.type || 'asset',
    version: item.version || '',
    time: toLocalTime(item.deployedAt),
  }
}

export default function AppSuiteOverviewPage() {
  const [templatesCount, setTemplatesCount] = useState(0)
  const [deviceTypesCount, setDeviceTypesCount] = useState(0)
  const [componentsCount, setComponentsCount] = useState(0)
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      listAppSuitePublishedTemplates(),
      listAppSuitePublishedDeviceTypeModals(),
      listAppSuitePublishedComponentStyles(),
      listAnnouncements(),
    ])
      .then(([templates, deviceTypes, components, published]) => {
        setTemplatesCount(templates.length)
        setDeviceTypesCount(deviceTypes.length)
        setComponentsCount(components.length)
        setAnnouncements(published.slice(0, 8).map(toAnnouncement))
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : '載入 Overview 失敗')
      })
      .finally(() => setLoading(false))
  }, [])

  const hasAnnouncements = useMemo(() => announcements.length > 0, [announcements.length])

  return (
    <div className="appsuite-overview appfactory-page-content">
      <section className="appsuite-overview-hero">
        <h2>App Suite Overview</h2>
        <p>這是 App Suite 專用總覽，資產來源僅使用 App Factory 已發佈內容。</p>
      </section>

      <section className="appsuite-overview-kpi-grid">
        <Link to="/appsuite/templates" className="appsuite-overview-kpi-card">
          <span>Published Templates</span>
          <strong>{templatesCount}</strong>
        </Link>
        <Link to="/appsuite/device-type-templates" className="appsuite-overview-kpi-card">
          <span>Published Device Types</span>
          <strong>{deviceTypesCount}</strong>
        </Link>
        <Link to="/appsuite/components" className="appsuite-overview-kpi-card">
          <span>Published Component Styles</span>
          <strong>{componentsCount}</strong>
        </Link>
        <Link to="/appsuite/appsuite-iam" className="appsuite-overview-kpi-card">
          <span>Users</span>
          <strong>Manage</strong>
        </Link>
      </section>

      <section className="appsuite-overview-announcement">
        <div className="appsuite-overview-announcement-header">
          <h3>New Available Assets</h3>
        </div>

        {loading ? <div className="appsuite-overview-placeholder">Loading...</div> : null}
        {error ? <div className="appsuite-overview-error">{error}</div> : null}
        {!loading && !error && !hasAnnouncements ? (
          <div className="appsuite-overview-placeholder">No new published assets yet.</div>
        ) : null}

        {!loading && !error && hasAnnouncements ? (
          <div className="appsuite-overview-announcement-list">
            {announcements.map((item) => {
              const typeInfo = ASSET_TYPE_LABELS[item.assetType] || { label: item.assetType, icon: 'extension' }
              return (
                <div key={item.id} className="appsuite-overview-announcement-item">
                  <div className="appsuite-overview-announcement-title-row">
                    <span className="material-symbols-rounded appsuite-overview-announcement-icon">{typeInfo.icon}</span>
                    <div className="appsuite-overview-announcement-title">{item.name}</div>
                    <span className="appsuite-overview-announcement-label published">{typeInfo.label}</span>
                  </div>
                  <div className="appsuite-overview-announcement-meta">
                    {item.version ? <span className="appsuite-overview-announcement-version">{item.version}</span> : null}
                    <span className="appsuite-overview-announcement-time">{item.time}</span>
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}
      </section>
    </div>
  )
}
